import React, { useEffect, useState } from 'react';
import { APPBACKGROUND_COLOR, MAINAPIURL } from '../appsettings';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Breadcrumb,
    Layout,
    Spin,
    Card,
    Row,
    Col,
    Table,
    Descriptions,
    Tag,
    Space,
    Button,
    Modal, // Added Modal
} from 'antd';
import axios from 'axios';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import { AlertError } from '../usableclasses';
import { PDFViewer } from '@react-pdf/renderer'; // Added PDFViewer
import OrderPrintDocument from './order-print'; // Import the new print component

const { Content } = Layout;

const OrderView = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [orderData, setOrderData] = useState(null);
    const [isPrintModalVisible, setIsPrintModalVisible] = useState(false); // State for print modal
    const { id } = useParams();
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");

    useEffect(() => {
        const fetchOrder = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${ApiURL}/orders/orderview/${id}`, {
                    headers: { 'SystemAccessToken': TOKEN }
                });
                if (response.data) {
                    setOrderData(response.data);
                } else {
                    AlertError('Failed to load order data');
                    setOrderData(null);
                }
            } catch (error) {
                AlertError("Failed to fetch order: " + (error.message || "Unknown error"));
                setOrderData(null);
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [id, ApiURL, TOKEN]);

    const handlePrintModalOpen = () => {
        setIsPrintModalVisible(true);
    };

    const handlePrintModalClose = () => {
        setIsPrintModalVisible(false);
    };

    const getStatusTag = (status) => {
        const statusUpper = (status || '').toUpperCase();
        let color = 'default';

        switch (statusUpper) {
            case 'SUBMITTED':
                color = 'cyan';
                break;
            case 'UNPAID':
                color = 'orange';
                break;
            case 'PAYMENT FAILED':
                color = 'volcano';
                break;
            case 'PAID':
                color = 'green';
                break;
            case 'PROCESSING':
                color = 'blue';
                break;
            case 'ORDER READY':
                color = 'purple';
                break;
            case 'COMPLETED':
                color = 'geekblue';
                break;
            case 'CANCELLED': // fallback spelling
                color = 'red';
                break;
            default:
                color = 'default';
                break;
        }

        return <Tag color={color}>{statusUpper || 'N/A'}</Tag>;
    };


    const orderColumns = [
        { title: 'CODE', dataIndex: 'productCode', key: 'productCode' },
        { title: 'NAME', dataIndex: 'productName', key: 'productName' },
        { title: 'SIZE', dataIndex: 'productSize', key: 'productSize' },
        { title: 'UOM', dataIndex: 'productUom', key: 'productUom' },
        { title: 'ORDER QTY', dataIndex: 'itemQty', key: 'itemQty', align: 'right' },
        { title: 'LINE TOTAL', dataIndex: 'itemSubTotal', key: 'itemSubTotal', render: (text) => parseFloat(text || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), align: 'right' },
        { title: 'POINTS', dataIndex: 'itemTotalPoints', key: 'itemTotalPoints', align: 'right' },
    ];

    const activityColumns = [
        { title: 'DATE TIME', dataIndex: 'logDateTime', key: 'logDateTime' },
        { title: 'ACTION', dataIndex: 'logActivity', key: 'logActivity' }
    ];

    return (
        <Spin spinning={loading} tip="Loading order details...">

            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: '16px 0' }}>
                        <Breadcrumb.Item><Link to={'/orders'}>MLM Orders</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>View</Breadcrumb.Item>
                        <Breadcrumb.Item>{orderData ? orderData.orderReferance : id}</Breadcrumb.Item>
                    </Breadcrumb>

                    <div>
                        {loading && !orderData ? (
                            <div style={{ textAlign: 'center', padding: '50px' }} />
                        ) : orderData ? (
                            <Space direction="vertical" style={{ width: "100%" }} size="large">
                                <Card
                                    title="Order Summary"
                                    size="small"
                                >
                                    {/* Removed button from here as it's moved to card 'extra' prop */}
                                    <Descriptions bordered column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }} layout="vertical" size="small">
                                        <Descriptions.Item label="Order Ref">{orderData.orderReferance || '-'}</Descriptions.Item>
                                        <Descriptions.Item label="Status">{getStatusTag(orderData.currentStage)}</Descriptions.Item>
                                        <Descriptions.Item label="Consultant ID"><Link to={`/mlm/userview/${orderData.consultionId}`}>{orderData.consultionId || '-'}</Link></Descriptions.Item>
                                        <Descriptions.Item label="Consultant Name">{orderData.orderBy || '-'}</Descriptions.Item>
                                        <Descriptions.Item label="Created Date">{orderData.createDateTime || '-'}</Descriptions.Item>
                                        <Descriptions.Item label="Platform">{orderData.platform || '-'}</Descriptions.Item>
                                        <Descriptions.Item label="Financial Year">{orderData.finYear || '-'}</Descriptions.Item>
                                        <Descriptions.Item label="System Year">{orderData.sysYear || '-'}</Descriptions.Item>
                                        <Descriptions.Item label="System Month">{orderData.sysMonth || '-'}</Descriptions.Item>
                                        <Descriptions.Item label="Total Points">{orderData.totalPoints || 0}</Descriptions.Item>
                                        <Descriptions.Item label="Total Products">{orderData.totalProductsCount || 0}</Descriptions.Item>
                                        <Descriptions.Item label="Subtotal (Items)">{parseFloat(orderData.totalProductsCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Descriptions.Item>
                                        <Descriptions.Item label="Shipping Cost">{parseFloat(orderData.totalShippingCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Descriptions.Item>
                                        <Descriptions.Item label="Grand Total">{parseFloat(orderData.totalCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Descriptions.Item>
                                        <Descriptions.Item label="Total Qty">{orderData.totalQtyCount || 0}</Descriptions.Item>
                                        <Descriptions.Item label="Total Wallet Paid">{parseFloat(orderData.totalWalletPaid || 0)}</Descriptions.Item>
                                        <Descriptions.Item label="Total To Be Paid">{parseFloat(orderData.totalToBePaid || 0)}</Descriptions.Item>
                                    </Descriptions>
                                </Card>

                                <Row gutter={16}>
                                    {/* Shipping Info */}
                                    <Col xs={24} md={24} lg={12}>
                                        <Card title="Shipping Information" size="small">
                                            <Descriptions
                                                layout="horizontal"
                                                size="small"
                                                column={2} // Fixed 2 columns for consistent layout
                                                labelStyle={{ fontWeight: 'bold', width: '150px' }}
                                                contentStyle={{ whiteSpace: 'pre-wrap' }}
                                            >
                                                <Descriptions.Item label="Shipping Method">
                                                    {orderData.shippingMethod || '-'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Shipping Address 1">
                                                    {orderData.shippingAddress1 || '-'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Shipping Address 2">
                                                    {orderData.shippingAddress2 || '-'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Shipping City">
                                                    {orderData.shippingCity || '-'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Shipping District">
                                                    {orderData.shippingDistrict || '-'}
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </Card>
                                    </Col>

                                    {/* Payment Info */}
                                    <Col xs={24} md={24} lg={12}>
                                        <Card title="Payment Information" size="small">
                                            <Descriptions
                                                layout="horizontal"
                                                size="small"
                                                column={2}
                                                labelStyle={{ fontWeight: 'bold', width: '150px' }}
                                                contentStyle={{ whiteSpace: 'pre-wrap' }}
                                            >
                                                <Descriptions.Item label="Method">
                                                    {orderData.paymentMethod || '-'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Transfer Type">
                                                    {orderData.paymentTransferType || '-'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Reference" span={3}>
                                                    {orderData.paymentReferance || '-'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Payment Log" span={1}>
                                                    {orderData.paymentLog || '-'}
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </Card>
                                    </Col>
                                </Row>


                                <Card title="Order Item List" size="small">
                                    <Table
                                        columns={orderColumns}
                                        dataSource={orderData.itemsList}
                                        rowKey="orderItemId"
                                        pagination={false}
                                        size="small"
                                        summary={() => {
                                            const handlingFee = orderData.totalHandlingFee || 0;
                                            const totalQty = orderData.totalQtyCount || 0;
                                            const subTotalItems = orderData.totalProductsCost || 0;
                                            const shippingCost = orderData.totalShippingCost || 0;
                                            const grandTotal = orderData.totalCost || (subTotalItems + shippingCost + handlingFee);
                                            const totalPoints = orderData.totalPoints || 0;

                                            return (
                                                <>
                                                    {/* Row for Subtotal of items */}
                                                    <Table.Summary.Row style={{ backgroundColor: "#f0f2f5" }}>
                                                        <Table.Summary.Cell index={0} colSpan={4} align="right"><strong>Subtotal (Items)</strong></Table.Summary.Cell>
                                                        <Table.Summary.Cell index={4} align="right"><strong>{totalQty}</strong></Table.Summary.Cell>
                                                        <Table.Summary.Cell index={5} align="right"><strong>{parseFloat(subTotalItems).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></Table.Summary.Cell>
                                                        <Table.Summary.Cell index={6} align="right"><strong>{totalPoints}</strong></Table.Summary.Cell>
                                                    </Table.Summary.Row>

                                                    {/* Row for Shipping Cost */}
                                                    <Table.Summary.Row style={{ backgroundColor: "#f0f2f5" }}>
                                                        <Table.Summary.Cell index={0} colSpan={5} align="right"><strong>Handling Fee</strong></Table.Summary.Cell>
                                                        <Table.Summary.Cell index={5} align="right"><strong>{parseFloat(handlingFee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></Table.Summary.Cell>
                                                        <Table.Summary.Cell index={6} />{/* Empty cell under POINTS column */}
                                                    </Table.Summary.Row>

                                                    {/* Row for Shipping Cost */}
                                                    <Table.Summary.Row style={{ backgroundColor: "#f0f2f5" }}>
                                                        <Table.Summary.Cell index={0} colSpan={5} align="right"><strong>Shipping Cost</strong></Table.Summary.Cell>
                                                        <Table.Summary.Cell index={5} align="right"><strong>{parseFloat(shippingCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></Table.Summary.Cell>
                                                        <Table.Summary.Cell index={6} />{/* Empty cell under POINTS column */}
                                                    </Table.Summary.Row>

                                                    {/* Row for Grand Total */}
                                                    <Table.Summary.Row style={{ backgroundColor: "#e6f7ff", fontWeight: "bold" }}>
                                                        <Table.Summary.Cell index={0} colSpan={5} align="right"><strong>Grand Total</strong></Table.Summary.Cell>
                                                        <Table.Summary.Cell index={5} align="right"><strong>{parseFloat(grandTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></Table.Summary.Cell>
                                                        <Table.Summary.Cell index={6} />{/* Empty cell under POINTS column */}
                                                    </Table.Summary.Row>
                                                </>
                                            );
                                        }}
                                    />
                                </Card>

                                <Card title="Activity History" size="small">
                                    <Table
                                        columns={activityColumns}
                                        dataSource={orderData.activityLog}
                                        pagination={false}
                                        rowKey="logDateTime"
                                        size="small"
                                    />
                                </Card>
                            </Space>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <p>Order not found or failed to load.</p>
                            </div>
                        )}
                    </div>

                    {/* Print Modal */}
                    <Modal
                        title="Print Order"
                        open={isPrintModalVisible}
                        onCancel={handlePrintModalClose}
                        footer={null} // No default OK/Cancel buttons
                        width="90%" // Or a fixed width like 1000
                        style={{ top: 20 }} // Optional: Adjust vertical position
                        destroyOnClose // Important to re-render PDFViewer if data changes
                    >
                        {orderData && (
                            <PDFViewer width="100%" height="600px" showToolbar={true}>
                                <OrderPrintDocument orderData={orderData} />
                            </PDFViewer>
                        )}
                    </Modal>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default OrderView;
